import * as React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Dispatch } from 'redux';

import {
	AppBar,
	Dialog,
	IconButton,
	Slide,
	Toolbar,
	WithStyles,
} from '@material-ui/core';
import { createStyles, withStyles } from '@material-ui/core';
import grey from '@material-ui/core/colors/grey';
import Paper from '@material-ui/core/Paper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import CloseIcon from '@material-ui/icons/Close';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import { StaticContext } from 'react-router';
import { IRootState } from 'src/reducers';
import { ICareerInfo } from '../actions/search';
import {
	IMaterialFile,
	ISkillPeriod,
	setSkillDetail,
} from '../actions/skillDetail';
import i18n from '../resources/i18n';
import ScenarioFactory from '../scenario/SkillDetailScenarioFactory';
import Config from '../site.config.json';
import CareerDetail from './CareerDetail';

const styles = (theme: Theme) =>
	createStyles({
		appBar: {
			position: 'relative',
			width: 'auto',
		},
		careerDialog: {
			width: '80%',
		},
		cellItemName: {
			backgroundColor: grey[300],
			minWidth: '160px',
			textAlign: 'right',
			width: '20%',
		},
		cellItemValue: {
			textAlign: 'left',
			width: '80%',
		},
		downloadButton: {
			cursor: 'pointer',
			marginLeft: 20,
			verticalAlign: 'middle',
		},
		fileLink: {
			marginLeft: 20,
		},
		flex: {
			flex: 1,
		},
		headerName: {
			backgroundColor: grey[300],
		},
		link: {
			color: 'rgb(0,136,204)',
			cursor: 'pointer',
			fontWeight: 'bold',
		},
		numValue: {
			textAlign: 'right',
		},
		paper: {
			overflowX: 'auto',
		},
		root: {
			flexGrow: 1,
			padding: 18,
		},
		scrollPaper: {
			alignItems: 'flex-start',
		},
		table: {
			minWidth: '700px',
		},
		tableClass: {
			minWidth: '1000px',
		},
		wrapDiv: {
			margin: '10px',
		},
	});

export interface ISkillDetailProps {
	skillId: string; // スキルID
}

interface IRouteParams {
	skill_id: string;
}

// tslint:disable-next-line:no-empty-interface
interface IRouteState {}

interface IState {
	skillId: string; // スキルID
	openCareerDetailDialog: boolean; // 研修詳細情報画面表示フラグ
	careerID: string; // 研修詳細情報画面表示用研修ID
}

/**
 * スキル詳細情報
 */
class SkillDetail extends React.Component<IProps, IState> {
	protected materialDownloadForm: HTMLFormElement | null;
	protected fileIndexInput: HTMLInputElement | null;
	protected fileTypeInput: HTMLInputElement | null;
	protected cookieNameInput: HTMLInputElement | null;

	public constructor(props: IProps) {
		super(props);

		this.state = {
			...props,
			careerID: '',
			openCareerDetailDialog: false,
		};

		// 関数紐づけ
		this.onClickCareerName = this.onClickCareerName.bind(this);
		this.handleCloseCareerDetailDialog = this.handleCloseCareerDetailDialog.bind(
			this
		);

		// データ取得処理
		if (props.skillId) {
			this.props.fetchSkillDetail(props.skillId, props.token);
		}
	}

	/**
	 * Lifecycle Methods
	 */
	public componentDidMount() {
		window.scrollTo({
			top: 0,
		});
		if (!this.props.match || !this.props.match.params) {
			return;
		}

		const skillId = this.props.match.params.skill_id;

		// データ取得処理
		this.props.fetchSkillDetail(skillId, this.props.token);
	}

	/**
	 * Lifecycle Methods
	 */
	public componentWillUnmount() {
		// データを破棄
		this.props.resetSkillDetail();
	}

	/**
	 * HTMLテーブルの内容を作成する
	 * @param key TableRowのキー
	 * @param itemName 項目名
	 * @param itemValue 項目値
	 */
	public createTrElm(key: string, itemName?: string, itemValue?: string) {
		const classes = this.props.classes;

		return (
			<TableRow key={key}>
				<TableCell className={classes.cellItemName}>
					{itemName}
				</TableCell>
				<TableCell className={classes.cellItemValue}>
					{itemValue}
				</TableCell>
			</TableRow>
		);
	}

	/**
	 * 一覧テーブルのセルを作成する
	 * @param label ラベル
	 * @param headerClassName HTMLのクラス名
	 * @param optionClass css名
	 */
	public createListCellElm(
		label?: string,
		headerClassName?: string,
		optionClass?: string
	) {
		if (headerClassName) {
			return (
				<TableCell
					className={headerClassName + ' ' + optionClass}
					size="small"
				>
					{label}
				</TableCell>
			);
		} else {
			return (
				<TableCell className={optionClass} size="small">
					{label}
				</TableCell>
			);
		}
	}

	/**
	 * 研修名リンクを作成する
	 * @param career
	 */
	public getCareerNameLink(career: ICareerInfo) {
		const classes = this.props.classes;
		const { careerID, careerTitle } = career;

		return (
			<TableCell size="small">
				<span
					className={classes.link}
					data-id={careerID}
					onClick={this.onClickCareerName}
				>
					{careerTitle}
				</span>
			</TableCell>
		);
	}

	/**
	 * urlが有る場合、リンクを表示する。
	 * urlがない場合、ラベルとして表示する。
	 * @param label
	 * @param url
	 */
	public getUrlLink(label?: string, url?: string) {
		const classes = this.props.classes;
		let elm;

		if (url && url !== '') {
			elm = (
				<span
					className={classes.link}
					data-url={url}
					onClick={this.onClickOpenNewTab}
				>
					{label}
				</span>
			);
		} else {
			elm = label;
		}

		return elm;
	}

	/**
	 * スキル関連資料ダウンロード
	 * @param event クリックイベント
	 */
	public downloadFile(event: React.MouseEvent<HTMLElement>) {
		const fileIndex = event.currentTarget.getAttribute('data-fileindex');
		const fileType = event.currentTarget.getAttribute('data-filetype');
		if (this.fileIndexInput != null) {
			let searchCondition = this.fileIndexInput.value;
			const dto = JSON.parse(searchCondition);
			dto.fileIndex = fileIndex;
			searchCondition = JSON.stringify(dto);
			this.fileIndexInput.value = searchCondition;
			if (fileType && this.fileTypeInput) {
				this.fileTypeInput.value = fileType;
			}
			if (fileIndex && this.cookieNameInput) {
				this.cookieNameInput.value = 'TSQ_FD_' + fileIndex;
			}
		}

		if (this.materialDownloadForm != null) {
			this.materialDownloadForm.submit();
		}
	}

	/**
	 * スキル関連資料セルの内容を作成する
	 * @param skillInfo スキル情報
	 */
	public getMaterialFiles(materialFileList?: IMaterialFile[]) {
		const classes = this.props.classes;
		const rows: JSX.Element[] = [];

		if (materialFileList && materialFileList.length > 0) {
			for (let i = 0; i < materialFileList.length; i++) {
				const materialFileDTO = materialFileList[i];
				const fileName = materialFileDTO.fileName;
				const fileType = materialFileDTO.fileType;
				const fileIndex = materialFileDTO.fileIndex;
				const buttonId = 'buttonId' + i;
				const linkId = 'linkId' + i;
				const row = (
					<div key={'div' + i}>
						<span>{fileName}</span>
						<span
							id={linkId}
							className={classes.link + ' ' + classes.fileLink}
							data-fileindex={fileIndex}
							data-filetype={fileType}
							// tslint:disable-next-line: jsx-no-bind
							onClick={this.downloadFile.bind(this)}
						>
							{fileName} {fileType}
						</span>
						<CloudDownloadIcon
							id={buttonId}
							data-fileindex={fileIndex}
							data-filetype={fileType}
							// tslint:disable-next-line: jsx-no-bind
							onClick={this.downloadFile.bind(this)}
							className={classes.downloadButton}
						/>
					</div>
				);
				rows.push(row);
			}
		}

		return rows;
	}

	public render() {
		const { classes, skillInfo } = this.props;
		const {
			periodInfoList,
			necessaryCareerList,
			materialFileList,
		} = skillInfo;

		return (
			<div className={classes.root}>
				<div className={classes.wrapDiv}>
					<Typography
						variant="h6"
						color="inherit"
						className={classes.flex}
					>
						{i18n.t('SkillDetail_0001')}
					</Typography>
					<Paper className={classes.paper}>
						<Table className={classes.table}>
							<TableBody>
								{this.createTrElm(
									'managementCode',
									i18n.t('SkillDetail_0002'),
									skillInfo.managementCode
								)}
								{this.createTrElm(
									'skillName',
									i18n.t('SkillDetail_0003'),
									skillInfo.skillName
								)}
								{this.createTrElm(
									'skillId',
									i18n.t('SkillDetail_0004'),
									skillInfo.skillId
								)}
								{this.createTrElm(
									'type',
									i18n.t('SkillDetail_0005'),
									skillInfo.type === '資格' ||
										skillInfo.type === '業務経験' ||
										skillInfo.type === 'スキル診断'
										? i18n.t(skillInfo.type, {
												ns: 'force',
										  })
										: skillInfo.type
								)}
								{this.createTrElm(
									'subType',
									i18n.t('SkillDetail_0006'),
									skillInfo.subType
								)}
								{this.createTrElm(
									'compulsoryID',
									i18n.t('SkillDetail_0007'),
									skillInfo.compulsoryID
								)}
								{this.createTrElm(
									'ranksFlag',
									i18n.t('SkillDetail_0008'),
									skillInfo.ranksFlag === '1'
										? i18n.t('SkillDetail_0009')
										: i18n.t('SkillDetail_0010')
								)}
								{this.createTrElm(
									'scoreRegisterFlag',
									i18n.t('SkillDetail_0011'),
									skillInfo.scoreRegisterFlag === '1'
										? i18n.t('SkillDetail_0009')
										: i18n.t('SkillDetail_0010')
								)}
								{this.createTrElm(
									'scoreItemsRegisterFlag',
									i18n.t('SkillDetail_0012'),
									skillInfo.scoreItemsRegisterFlag === '1'
										? i18n.t('SkillDetail_0009')
										: i18n.t('SkillDetail_0010')
								)}
								{this.createTrElm(
									'target',
									i18n.t('SkillDetail_0013'),
									skillInfo.target
								)}
								{this.createTrElm(
									'skillManager',
									i18n.t('SkillDetail_0014'),
									skillInfo.skillManager
								)}
							</TableBody>
						</Table>
					</Paper>
				</div>
				<div className={classes.wrapDiv}>
					<Typography
						variant="h6"
						color="inherit"
						className={classes.flex}
					>
						{i18n.t('SkillDetail_0015')}
					</Typography>
					<Paper className={classes.paper}>
						<Table className={classes.tableClass}>
							<colgroup>
								<col width={'20%'} />
								<col width={'*'} />
								<col width={'25%'} />
								<col width={'10%'} />
								<col width={'10%'} />
								<col width={'25%'} />
							</colgroup>
							<TableHead>
								<TableRow id={'skillPeriodListHeader'}>
									{this.createListCellElm(
										i18n.t('SkillDetail_0016'),
										classes.headerName
									)}
									{this.createListCellElm(
										i18n.t('SkillDetail_0017'),
										classes.headerName
									)}
									{this.createListCellElm(
										i18n.t('SkillDetail_0018'),
										classes.headerName
									)}
									{this.createListCellElm(
										i18n.t('SkillDetail_0019'),
										classes.headerName
									)}
									{this.createListCellElm(
										i18n.t('SkillDetail_0020'),
										classes.headerName
									)}
									{this.createListCellElm(
										i18n.t('SkillDetail_0021'),
										classes.headerName
									)}
								</TableRow>
							</TableHead>
							<TableBody>
								{periodInfoList && periodInfoList.length > 0
									? periodInfoList.map(
											(item: ISkillPeriod) => {
												// 実施期間
												let periodDate =
													item.periodDate;
												if (
													periodDate &&
													periodDate.indexOf('～') !==
														-1
												) {
													periodDate = periodDate.replace(
														'～',
														i18n.t('～', {
															ns: 'force',
														})
													);
												}
												// 申込期間
												let applDate = item.applDate;
												if (
													applDate &&
													applDate.indexOf('～') !==
														-1
												) {
													applDate = applDate.replace(
														'～',
														i18n.t('～', {
															ns: 'force',
														})
													);
												}
												return (
													<TableRow
														key={item.periodId}
													>
														{this.createListCellElm(
															item.periodName &&
																item.periodId ===
																	'CL000000'
																? i18n.t(
																		item.periodName,
																		{
																			ns:
																				'force',
																		}
																  )
																: item.periodName
														)}
														{this.createListCellElm(
															item.periodId
														)}
														{this.createListCellElm(
															periodDate
														)}
														{this.createListCellElm(
															item.cost,
															undefined,
															classes.numValue
														)}
														{this.createListCellElm(
															item.selfCost,
															undefined,
															classes.numValue
														)}
														{this.createListCellElm(
															applDate
														)}
													</TableRow>
												);
											},
											this
									  )
									: null}
							</TableBody>
						</Table>
					</Paper>
				</div>
				{skillInfo.scoreRegisterFlag === '1' ? (
					''
				) : (
					<div className={classes.wrapDiv}>
						<Typography
							variant="h6"
							color="inherit"
							className={classes.flex}
						>
							{i18n.t('SkillDetail_0022')}
						</Typography>
						<Paper className={classes.paper}>
							<Table className={classes.tableClass}>
								<colgroup>
									<col width={'20%'} />
									<col width={'10%'} />
									<col width={'15%'} />
									<col width={'15%'} />
									<col width={'*'} />
									<col width={'*'} />
								</colgroup>
								<TableHead>
									<TableRow id={'skillCareerListHeader'}>
										{this.createListCellElm(
											i18n.t('SkillDetail_0023'),
											this.props.classes.headerName
										)}
										{this.createListCellElm(
											i18n.t('SkillDetail_0024'),
											this.props.classes.headerName
										)}
										{this.createListCellElm(
											i18n.t('SkillDetail_0025'),
											this.props.classes.headerName
										)}
										{this.createListCellElm(
											i18n.t('SkillDetail_0006'),
											this.props.classes.headerName
										)}
										{this.createListCellElm(
											i18n.t('SkillDetail_0013'),
											this.props.classes.headerName
										)}
										{this.createListCellElm(
											i18n.t('SkillDetail_0026'),
											this.props.classes.headerName
										)}
									</TableRow>
								</TableHead>
								<TableBody>
									{necessaryCareerList &&
									necessaryCareerList.length > 0
										? necessaryCareerList.map(
												(item: ICareerInfo) => {
													return (
														<TableRow
															key={item.careerID}
														>
															{this.getCareerNameLink(
																item
															)}
															{this.createListCellElm(
																item.careerID
															)}
															{this.createListCellElm(
																item.careerTypeName
																	? i18n.t(
																			item.careerTypeName,
																			{
																				ns:
																					'force',
																			}
																	  )
																	: item.careerTypeName
															)}
															{this.createListCellElm(
																item.careerSubType
															)}
															{this.createListCellElm(
																item.target
															)}
															{this.createListCellElm(
																item.organizer
															)}
														</TableRow>
													);
												},
												this
										  )
										: null}
								</TableBody>
							</Table>
						</Paper>
					</div>
				)}
				<div className={classes.wrapDiv}>
					<Typography
						variant="h6"
						color="inherit"
						className={classes.flex}
					>
						{i18n.t('SkillDetail_0027')}
					</Typography>
					<Paper className={classes.paper}>
						<Table className={classes.table}>
							<TableBody>
								{this.createTrElm(
									'purpose',
									i18n.t('SkillDetail_0028'),
									skillInfo.purpose
								)}
								{this.createTrElm(
									'outline',
									i18n.t('SkillDetail_0029'),
									skillInfo.outline
								)}
								{this.createTrElm(
									'credit',
									i18n.t('SkillDetail_0030'),
									skillInfo.credit
										? skillInfo.credit.toString()
										: ''
								)}
								<TableRow key="materialFileList">
									<TableCell className={classes.cellItemName}>
										{i18n.t('SkillDetail_0031')}
									</TableCell>
									<TableCell
										className={classes.cellItemValue}
									>
										{this.getMaterialFiles(
											materialFileList
										)}
									</TableCell>
								</TableRow>
								<TableRow key="vendor">
									<TableCell className={classes.cellItemName}>
										{i18n.t('SkillDetail_0032')}
									</TableCell>
									<TableCell
										className={classes.cellItemValue}
									>
										{this.getUrlLink(
											skillInfo.vendor,
											skillInfo.vendorURL
										)}
									</TableCell>
								</TableRow>
								{this.createTrElm(
									'inquiry',
									i18n.t('SkillDetail_0033'),
									skillInfo.inquiry
								)}
								{this.createTrElm(
									'applicationFlg',
									i18n.t('SkillDetail_0034'),
									skillInfo.applicationFlg === '1'
										? i18n.t('SkillDetail_0035')
										: i18n.t('SkillDetail_0036')
								)}
								<TableRow key="reference">
									<TableCell className={classes.cellItemName}>
										{i18n.t('SkillDetail_0037')}
									</TableCell>
									<TableCell
										className={classes.cellItemValue}
									>
										{this.getUrlLink(
											skillInfo.reference,
											skillInfo.referenceURL
										)}
									</TableCell>
								</TableRow>
								{this.createTrElm(
									'comment',
									i18n.t('SkillDetail_0038'),
									skillInfo.comment
								)}
							</TableBody>
						</Table>
					</Paper>
				</div>
				<form
					method="POST"
					action={
						Config.tsqPath +
						'/tsq/pkg/action/el/materialFileDownload.action'
					}
					ref={form => (this.materialDownloadForm = form)}
					target="_blank"
				>
					<input
						type="hidden"
						name="searchCondition"
						value={`{"careerID":"${
							skillInfo.skillId
						}","fileIndex":"0"}`}
						ref={fileIndexInput =>
							(this.fileIndexInput = fileIndexInput)
						}
					/>
					<input
						type="hidden"
						name="__tsqCookieName"
						value="TSQ_FD_0"
						ref={cookieName => (this.cookieNameInput = cookieName)}
					/>
					<input
						type="hidden"
						name="__tsqFileType"
						value="*"
						ref={fileType => (this.fileTypeInput = fileType)}
					/>
				</form>
				<Dialog
					fullScreen={false}
					TransitionComponent={Transition}
					open={this.state.openCareerDetailDialog}
					onClose={this.handleCloseCareerDetailDialog}
					fullWidth={true}
					maxWidth={false}
					classes={{
						paperFullWidth: classes.careerDialog,
						scrollPaper: classes.scrollPaper,
					}}
				>
					<AppBar className={classes.appBar}>
						<Toolbar>
							<IconButton
								color="inherit"
								aria-label="Close"
								onClick={this.handleCloseCareerDetailDialog}
							>
								<CloseIcon />
							</IconButton>
							<Typography variant="h6" color="inherit">
								{i18n.t('SkillDetail_0039')}
							</Typography>
						</Toolbar>
					</AppBar>
					<CareerDetail careerID={this.state.careerID} />
				</Dialog>
			</div>
		);
	}

	/**
	 * リンク先クリックハンドラ
	 * @param e クリックイベント
	 */
	protected onClickOpenNewTab(e: React.MouseEvent<HTMLElement>) {
		const url = e.currentTarget.getAttribute('data-url');
		if (url && url !== '') {
			window.open(url, '_blank');
		}
	}

	/**
	 * 研修名クリックハンドラ
	 * @param e クリックイベント
	 */
	protected onClickCareerName(e: React.MouseEvent<HTMLElement>) {
		const careerId = e.currentTarget.getAttribute('data-id');

		if (careerId) {
			this.setState({
				careerID: careerId,
				openCareerDetailDialog: true,
			});
		}
	}

	/**
	 * 研修詳細情報画面閉じるハンドラ
	 */
	protected handleCloseCareerDetailDialog = () => {
		this.setState({ careerID: '', openCareerDetailDialog: false });
	};
}

const mapStateToProps = (state: IRootState) => ({
	skillInfo: state.skillDetail.skillInfo,
	token: state.session.token,
	userAttr: state.session.userAttr,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
	fetchSkillDetail: (skillId: string, token: string) => {
		const scenario = ScenarioFactory.createFetchSkillDetail(
			dispatch,
			token
		);
		scenario.run(skillId);
	},
	resetSkillDetail: () => {
		dispatch(
			setSkillDetail({
				skillInfo: {
					skillId: '',
				},
			})
		);
	},
});

const Transition: React.FC<any> = props => {
	return <Slide direction="up" {...props} />;
};

const mergeProps = (
	state: IStateProps,
	events: IDispatchProps,
	ownProps: IOwnProps
) => ({
	...state,
	...events,
	...ownProps,
});

type IStateProps = ReturnType<typeof mapStateToProps>;
type IDispatchProps = ReturnType<typeof mapDispatchToProps>;
type IOwnProps = WithStyles<typeof styles> &
	RouteComponentProps<IRouteParams, StaticContext, IRouteState> &
	ISkillDetailProps;
type IProps = ReturnType<typeof mergeProps>;

const Connected = connect(
	mapStateToProps,
	mapDispatchToProps,
	mergeProps
)(SkillDetail);

const Styled = withStyles(styles)(Connected);
const Export: React.ComponentType<ISkillDetailProps> = Styled;

export default Export;



// WEBPACK FOOTER //
// ./src/containers/SkillDetail.tsx